import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useChatTranscript } from "./useChatTranscript";
import { TokenCounter } from "./TokenCounter";
import { useAgentBridge } from "../app/AgentBridge";
import type { AgentMode } from "../api/types";

interface Props {
  mode: AgentMode;
  enabled: boolean;
  showVerbose?: boolean;
}

export function ChatPanel({ mode, enabled, showVerbose = true }: Props) {
  const transcript = useChatTranscript();
  const { status, send, subscribe } = useAgentBridge();

  useEffect(() => subscribe(transcript.handleServer), [subscribe, transcript.handleServer]);

  const [draft, setDraft] = useState("");
  const [verbose, setVerbose] = useState(showVerbose);
  const messagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [transcript.bubbles]);

  const visible = useMemo(
    () => (verbose ? transcript.bubbles : transcript.bubbles.filter((b) => b.role !== "thinking" && b.role !== "system")),
    [verbose, transcript.bubbles],
  );

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!draft.trim() || transcript.running) return;
    const text = draft;
    if (!send({ type: "message", content: text, mode })) return;
    transcript.appendUser(text);
    setDraft("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  }

  function cancel() {
    if (!transcript.running) return;
    send({ type: "cancel" });
  }

  return (
    <section className="chat-section">
      <div className="chat-header-row">
        <label className="verbose-toggle">
          <input type="checkbox" checked={verbose} onChange={(e) => setVerbose(e.target.checked)} />
          <span>Verbose (alle Agent-Ausgaben)</span>
        </label>
        <TokenCounter usage={transcript.usage} />
        <span className={`ws-status ws-status-${status}`} title={`WebSocket: ${status}`}>{status}</span>
        {transcript.running && (
          <button type="button" className="btn btn-secondary" onClick={cancel}>Abbrechen</button>
        )}
      </div>

      <div className="messages" ref={messagesRef} role="log" aria-live="polite">
        {visible.length === 0 && (
          <div className="messages-empty">Stelle eine Frage oder starte einen Wizard im linken Bereich.</div>
        )}
        {visible.map((b) => (
          <div key={b.id} className={`bubble bubble-${b.role}${b.streaming ? " bubble-streaming" : ""}`}>
            {b.text}
          </div>
        ))}
      </div>

      <form className="input-row" onSubmit={submit}>
        <textarea
          className="chat-input"
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={enabled ? "Nachricht an den Agenten…" : "Nicht verbunden"}
          disabled={!enabled}
          aria-label="Nachricht"
        />
        <button type="submit" className="btn btn-primary" disabled={!enabled || transcript.running}>
          Senden
        </button>
      </form>
    </section>
  );
}
